import { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GetAgentThreadsResponse } from '@dreamai/types';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import api from '../../../api';

/**
 * Returns the number of project threads (for the given agent) that the user
 * has unread activity on.
 *
 * Project thread IDs are fetched once on mount / agentId change. The unread
 * count itself is recomputed every render against the live unread set from
 * ThreadReadsProvider, so the badge ticks down immediately when the user
 * opens a project and that thread gets marked read via Pusher — no refetch
 * needed.
 *
 * The set of project IDs only goes stale when a project is created or
 * archived. Archive uses a full page reload (see `CanvasAssets`'
 * `handleArchiveProject`), and creation isn't urgent enough to warrant
 * extra plumbing — happy to revisit if it becomes noticeable.
 */
export const useProjectsUnreadCount = (agentId: string | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getAllUnreadThreads } = useThreadReads();
  const [projectThreadIds, setProjectThreadIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!agentId) {
      setProjectThreadIds(new Set());
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetAgentThreadsResponse>('/agents/threads', {
          params: {
            agent_id: agentId,
            kind: 'Project',
            excludePinned: 'true',
            excludeShared: 'true',
            excludeSubThreads: 'true',
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const threads = res.data.success ? res.data.threads : [];
        setProjectThreadIds(
          new Set(
            threads.filter((t) => !t.is_archived_thread).map((t) => t.id),
          ),
        );
      } catch (err) {
        console.error('useProjectsUnreadCount fetch error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId, getAccessTokenSilently]);

  const unreadThreads = getAllUnreadThreads();
  const unreadCount = useMemo(
    () => unreadThreads.filter((t) => projectThreadIds.has(t.thread_id)).length,
    [unreadThreads, projectThreadIds],
  );

  return { unreadCount };
};
